import {
  useQuery,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
  useQueryClient,
  useMutation,
} from '@tanstack/react-query'
import supabase from 'lib/supabase-browser'
import { apiRequest } from './util'

// React Query client
const client = new QueryClient()

/**** USERS ****/

// Fetch user data
// Note: This is called automatically in `auth.js` and data is merged into `auth.user`
export function useUser(uid) {
  // Manage data fetching with React Query: https://react-query.tanstack.com/overview
  return useQuery(
    // Unique query key: https://react-query.tanstack.com/guides/query-keys
    ['user', { uid }],
    // Query function that fetches data
    async () => {
      const { data, error } = await supabase
        .from('users')
        .select(`*`)
        .eq('id', uid)
        .maybeSingle()
      if (error) throw error
      return data
    },
    // Only call query function if we have a `uid`
    { enabled: !!uid }
  )
}

// Fetch user data (non-hook)
// Useful if you need to fetch data from outside of a component
export async function getUser(uid) {
  const { data, error } = await supabase
    .from('users')
    .select(`*, customers ( * )`)
    .eq('id', uid)
    .single()
  if (error) throw error
  return data
}

// Update an existing user
export async function updateUser(uid, data) {
  const response = await supabase
    .from('users')
    .update(data)
    .eq('id', uid)
    .then(handle)
  // Invalidate and refetch queries that could have old data
  await client.invalidateQueries(['user', { uid }])
  return response
}

export async function requestTranscriptionAPI({
  submission,
  session,
  uid,
  type,
}) {
  const response = await fetch('/api/transcribe', {
    method: 'POST',
    body: JSON.stringify({
      submission_id: submission.id,
      upload_method: type,
    }),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
  })

  const transcription = await response.json()
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(['submission', { id: submission.id }]),
    client.invalidateQueries(['submissions']),
    client.invalidateQueries(['user', { uid }]),
  ])

  return transcription
}

// Create a new item
export async function createItem(data) {
  const response = await supabase.from('items').insert([data]).then(handle)
  // Invalidate and refetch queries that could have old data
  await client.invalidateQueries(['items'])
  return response
}

// Update an item
export async function updateItem(id, data) {
  const response = await supabase
    .from('items')
    .update(data)
    .eq('id', id)
    .then(handle)
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(['item', { id }]),
    client.invalidateQueries(['items']),
  ])
  return response
}

// Delete an item
export async function deleteItem(id) {
  const response = await supabase
    .from('items')
    .delete()
    .eq('id', id)
    .then(handle)
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(['item', { id }]),
    client.invalidateQueries(['items']),
  ])
  return response
}

export async function fetchSubmissions({ page, perPage, projectId }) {
  const {
    data: submissions,
    error,
    count,
  } = await supabase
    .from('submissions')
    .select(
      'id, filename, created_at, status, podcast_id, title, duration_in_hours, upload_method, users(id, email)',
      {
        count: 'exact',
      }
    )
    .eq('project_id', projectId)
    .eq('archived', false)
    .neq('status', 'pending')
    .range((page - 1) * perPage, page * perPage - 1)
    .order('created_at', { ascending: false })
  if (error) {
    console.error('Error fetching submissions:', error.message)
    throw new Error(error.message)
  }

  // format the created_at date
  submissions?.forEach((submission) => {
    submission.created_at = new Date(submission.created_at!).toISOString()
  })

  return { submissions, count: count || 0 }
}

export const checkIfUserExistsByEmail = async (email) => {
  // make a request to /api/get-user-by-email
  const resp = await apiRequest('get-user-exists-by-email', 'POST', { email })
  return resp
}

export const useFindSubmissions = (
  search: string | undefined,
  projectId: number
) => {
  const queryClient = useQueryClient()

  return useQuery(
    ['submissions', { search }],
    async () => {
      // add a + between each word and also remove any special characters but don't add a + at the beginning or end
      const cleanedSearch = search?.trim().replace(/\s+/g, '+')

      const { data, error } = await supabase.rpc(
        'search_submissions_by_title_or_filename',
        {
          prefix: cleanedSearch || '',
          project_id: projectId,
        }
      )

      if (error) throw error
      return data
    },
    {
      enabled: !!search || search === '',
      retry: false,
      onSuccess: (data) => {
        if (data.length === 1) {
          queryClient.setQueryData(['submission', { id: data[0].id }], data[0])
        }
      },
    }
  )
}

export const useWritingTemplates = ({ type = 'all' }) => {
  return useQuery(
    ['writing-templates', type],
    async () => {
      if (type === 'all') {
        const { data, error } = await supabase
          .from('writing_templates')
          .select(
            'id, created_at, owner, title, type, template, source_url, category'
          )

        if (error) {
          console.error('Error fetching writing templates:', error.message)
          throw new Error(error.message)
        }

        return data
      } else {
        const { data, error } = await supabase
          .from('writing_templates')
          .select(
            'id, created_at, owner, title, type, template, source_url, category'
          )
          .eq('type', type)

        if (error) {
          console.error('Error fetching writing templates:', error.message)
          throw new Error(error.message)
        }

        return data
      }
    },
    { enabled: !!type }
  )
}

export const useSubmissionsCount = () => {
  return useQuery(
    ['submissions-count'],
    async () => {
      const { count, error } = await supabase
        .from('submissions')
        .select('id', { count: 'exact' })
        .eq('archived', false)
      if (error) {
        throw error
      }
      return count
    },
    {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  )
}

export const useOutputsForSubmission = (submissionId) => {
  return useQuery(
    ['outputs', { submissionId }],
    async () => {
      const { data, error } = await supabase
        .from('outputs')
        .select('*, content_templates ( * )')
        .eq('submission_id', submissionId)

      if (error) {
        console.error('Error fetching outputs:', error.message)
        throw new Error(error.message)
      }

      return data
    },
    { enabled: !!submissionId }
  )
}

export async function getSubmissionIncludingResponse(submissionId, type) {
  const { data: submission, error } = await supabase
    .from('submissions')
    .select(type)
    .eq('id', submissionId)
    .single()

  if (error) {
    console.error('Error fetching submission article:', error.message)
    throw new Error(error.message)
  }

  const { data: responseObject, error: responseError } = await supabase
    .from('responses')
    .select('response')
    .eq('id', Number(submission![type]))
    .single()

  if (responseError) {
    throw new Error(responseError.message)
  }

  return { submission, response: responseObject?.response }
}

export function useResponse(submissionId, type) {
  return useQuery(
    ['responses', { submissionId, type }],
    async () => {
      try {
        const result = await getSubmissionIncludingResponse(
          submissionId,
          `${type}_active`
        )
        if (result.response == null) {
          throw new Error('Response is null')
        }
        return result
      } catch (error) {
        // If response is null or there's an error, fetch without the _active suffix
        return getSubmissionIncludingResponse(submissionId, type)
      }
    },
    {
      enabled: !!submissionId,
    }
  )
}

export const resetResponse = async (submissionId, type) => {
  const { data: submission, error } = await supabase
    .from('submissions')
    .select()
    .eq('id', submissionId)
    .single()

  if (error) {
    console.error('Error fetching submission article:', error.message)
    throw new Error(error.message)
  }

  const { response: originalResponse } = await getSubmissionIncludingResponse(
    submissionId,
    type
  )

  await updateResponse(submissionId, `${type}_active`, originalResponse)

  await Promise.all([
    client.invalidateQueries(['responses', { submissionId, type }]),
    client.invalidateQueries([
      'responses',
      { submissionId, type: `${type}_active` },
    ]),
    client.invalidateQueries(['responses']),
  ])

  return { submission, response: originalResponse }
}

export async function updateSubmissionResponse(submissionId, type, response) {
  const { data, error } = await supabase
    .from('submissions')
    .select()
    .eq('id', submissionId)
    .maybeSingle()

  if (error) {
    console.error('Error fetching submission:', error.message)
    throw new Error(error.message)
  }
  let responseObject
  if (!data || !data[type]) {
    const { data: newResponse, error: newResponseError } = await supabase
      .from('responses')
      .insert([{ response, submission_id: submissionId, owner: data?.owner }])
      .select('id')
      .single()

    responseObject = newResponse

    if (newResponseError) {
      console.error('Error creating new response:', newResponseError.message)
      throw new Error(newResponseError.message)
    }

    const { data: newSubmission, error: newSubmissionError } = await supabase
      .from('submissions')
      .update({ [type]: newResponse.id })
      .eq('id', submissionId)
      .single()

    if (newSubmissionError) {
      console.error(
        'Error updating submission with new response:',
        newSubmissionError.message
      )
      throw new Error(newSubmissionError.message)
    }
  } else {
    const { data: existingData, error: responseError } = await supabase
      .from('responses')
      .update({ response })
      .eq('id', data[type])
      .single()

    if (responseError) {
      console.error(`Error updating ${type} response:`, responseError.message)
      throw new Error(responseError.message)
    }
    responseObject = existingData
  }

  return responseObject
}

export async function updateResponse(submissionId, type, response) {
  await updateSubmissionResponse(submissionId, type, response)
  // Invalidate and refetch queries that could have old data
  await Promise.all([
    client.invalidateQueries(['responses', { submissionId, type }]),
    client.invalidateQueries(['responses']),
  ])
}
/**** HELPERS ****/

// Get response data or throw error if there is one
function handle(response) {
  if (response.error) throw response.error
  return response.data
}

// React Query context provider that wraps our app
export function QueryClientProvider(props) {
  return (
    <QueryClientProviderBase client={client}>
      {props.children}
    </QueryClientProviderBase>
  )
}
